import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { Check, Code, Eye, RefreshCw, Search, X } from 'lucide-react';

import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { DateRangeFilter } from '../../components/admin/DateRangeFilter';
import { SortableHeaderCell } from '../../components/admin/SortableHeaderCell';
import { brandChipSx } from '../../components/admin/brandChip';
import { DateRangeValue, EMPTY_DATE_RANGE, dateRangeToIso } from '../../components/admin/dateRange';
import { nextSort } from '../../components/admin/nextSort';
import { useDebouncedValue } from '../../components/admin/useDebouncedValue';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { emailSubscriptionReducer } from '../../store/reducers/emailSubscription.reducer';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { userReducer } from '../../store/reducers/user.reducer';
import {
  getActivateError,
  getActivateSuccess,
  getEmailSubscriptions,
  getEmailSubscriptionsError,
  getIsActivatingSubscription,
  getIsLoadingEmailSubscriptions,
  getIsSendingEmail,
  getSendEmailError,
  getSendEmailSuccess,
} from '../../store/selectors/emailSubscription.selector';
import { getAllUsers, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { greyP5 } from '../../theme';
import {
  EmailSubscriptionListItem,
  EmailSubscriptionQuery,
  EmailSubscriptionSort,
  EmailSubscriptionType,
} from '../../types/emailSubscription';
import { SortDirection } from '../../types/subscription';
import { User } from '../../types/user';

const { loadUsers } = userReducer.actions;
const { loadEmailSubscriptions, sendEmail, resetSendEmailStatus, activateEmailSubscription, resetActivateStatus } =
  emailSubscriptionReducer.actions;
const { showToast } = toastReducer.actions;

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
/** Text columns start A→Z, dates and statuses newest / active first. */
const DEFAULT_DIRECTION: Record<EmailSubscriptionSort, SortDirection> = {
  EMAIL: 'ASC',
  NAME: 'ASC',
  TYPE: 'ASC',
  ACTIVE: 'DESC',
  CREATED_AT: 'DESC',
  UPDATED_AT: 'DESC',
};

type StatusFilter = '' | 'active' | 'inactive';

const filterFieldSx = { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)' } };
const mutedSx = { color: 'rgba(255, 255, 255, 0.5)' };

const formatDate = (iso: string) => new Date(iso).toLocaleDateString();
const formatDateTime = (iso: string) => new Date(iso).toLocaleString();

export const EmailSubscriptionsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const allUsers = useAppSelector(getAllUsers);

  const subscriptions = useAppSelector(getEmailSubscriptions);
  const isLoading = useAppSelector(getIsLoadingEmailSubscriptions);
  const error = useAppSelector(getEmailSubscriptionsError);
  const isSending = useAppSelector(getIsSendingEmail);
  const sendError = useAppSelector(getSendEmailError);
  const sendSuccess = useAppSelector(getSendEmailSuccess);
  const isActivating = useAppSelector(getIsActivatingSubscription);
  const activateSuccess = useAppSelector(getActivateSuccess);
  const activateError = useAppSelector(getActivateError);

  // Filters, sorting and paging; all applied on the server
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const [filterType, setFilterType] = useState<EmailSubscriptionType | ''>('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('');
  const [subscribed, setSubscribed] = useState<DateRangeValue>(EMPTY_DATE_RANGE);
  const [sort, setSort] = useState<{ sort: EmailSubscriptionSort; direction: SortDirection }>({
    sort: 'CREATED_AT',
    direction: 'DESC',
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [activatingId, setActivatingId] = useState<number | null>(null);

  // Send email modal
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendType, setSendType] = useState<EmailSubscriptionType | ''>('');
  const [sendEmails, setSendEmails] = useState<string[]>([]);
  const [pasteEmails, setPasteEmails] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendHtmlContent, setSendHtmlContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const isAdmin = !!user && user.roles.includes('ADMIN');

  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  // Users are only needed for the send-email autocomplete
  useEffect(() => {
    if (isAdmin) {
      dispatch(loadUsers());
    }
  }, [dispatch, isAdmin]);

  // A new search, filter or page size starts again from the first page. Reset while rendering (not in an
  // effect) so the list is requested once, for page 0, instead of first for the old page.
  const filtersKey = [debouncedSearch, filterType, filterStatus, subscribed.from, subscribed.to, pageSize].join('|');
  const [pageFiltersKey, setPageFiltersKey] = useState(filtersKey);
  if (pageFiltersKey !== filtersKey) {
    setPageFiltersKey(filtersKey);
    setPage(0);
  }

  const query: EmailSubscriptionQuery = useMemo(() => {
    const subscribedIso = dateRangeToIso(subscribed);
    return {
      type: filterType || undefined,
      active: filterStatus === '' ? undefined : filterStatus === 'active',
      search: debouncedSearch || undefined,
      subscribedFrom: subscribedIso.from,
      subscribedTo: subscribedIso.to,
      sort: sort.sort,
      direction: sort.direction,
      page,
      pageSize,
    };
  }, [filterType, filterStatus, debouncedSearch, subscribed, sort, page, pageSize]);

  useEffect(() => {
    if (isAdmin) {
      dispatch(loadEmailSubscriptions(query));
    }
  }, [dispatch, isAdmin, query]);

  useEffect(() => {
    if (activateSuccess) {
      dispatch(showToast({ message: 'emailSubscriptions.activationSuccess', type: 'success' }));
      dispatch(resetActivateStatus());
      setActivatingId(null);
    }
  }, [activateSuccess, dispatch]);

  useEffect(() => {
    if (activateError) {
      dispatch(showToast({ message: activateError, type: 'error' }));
      dispatch(resetActivateStatus());
      setActivatingId(null);
    }
  }, [activateError, dispatch]);

  const handleSort = (column: EmailSubscriptionSort) => {
    setSort((current) => nextSort(current, column, DEFAULT_DIRECTION[column]));
    setPage(0);
  };

  const handleActivate = (id: number) => {
    setActivatingId(id);
    dispatch(activateEmailSubscription(id));
  };

  const rows = subscriptions?.content ?? [];
  const totalElements = subscriptions?.totalElements ?? 0;
  const hasFilters = !!debouncedSearch || !!filterType || !!filterStatus || !!subscribed.from || !!subscribed.to;

  const handleSendEmails = () => {
    if (!sendSubject.trim() || !sendHtmlContent.trim()) {
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const pastedEmailsList = pasteEmails
      .split('\n')
      .map((e) => e.trim())
      .filter((e) => e !== '');

    const allEmails = [...sendEmails, ...pastedEmailsList];
    const invalidEmails = allEmails.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      alert(`${t('emailSubscriptions.invalidEmails')}${invalidEmails.join(', ')}`);
      return;
    }

    if (!sendType && allEmails.length === 0) {
      alert(t('emailSubscriptions.noEmailsProvided'));
      return;
    }

    dispatch(
      sendEmail({
        subscriptionType: sendType || null,
        emails: allEmails.length > 0 ? allEmails : null,
        subject: sendSubject,
        htmlContent: sendHtmlContent,
      }),
    );
  };

  const handleCloseSendModal = () => {
    setSendModalOpen(false);
    setSendType('');
    setSendEmails([]);
    setPasteEmails('');
    setSendSubject('');
    setSendHtmlContent('');
    dispatch(resetSendEmailStatus());
  };

  if (!user || !user.roles.includes('ADMIN')) {
    return null;
  }

  const renderAccount = (sub: EmailSubscriptionListItem) => {
    if (sub.userId === null) {
      return (
        <Tooltip title={t('emailSubscriptions.emailOnlyHint')}>
          <Chip
            size="small"
            label={t('emailSubscriptions.emailOnly')}
            sx={brandChipSx('outlined', { color: 'rgba(255,255,255,0.6)' })}
          />
        </Tooltip>
      );
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {sub.userFullName || t('emailSubscriptions.userNumber', { id: sub.userId })}
        </Typography>
        <Typography variant="caption" sx={mutedSx}>
          #{sub.userId}
        </Typography>
      </Box>
    );
  };

  const renderStatus = (sub: EmailSubscriptionListItem) => {
    if (sub.active) {
      return <Chip size="small" color="success" label={t('emailSubscriptions.active')} />;
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, maxWidth: 260 }}>
        <Chip size="small" label={t('emailSubscriptions.unsubscribed')} />
        {sub.unsubscribeReason && (
          <Tooltip title={sub.unsubscribeReason}>
            <Typography variant="caption" noWrap sx={{ ...mutedSx, fontStyle: 'italic', maxWidth: '100%' }}>
              “{sub.unsubscribeReason}”
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  };

  const sortProps = { sort: sort.sort, direction: sort.direction, onSort: handleSort };

  return (
    <Box>
      <AdminPageHeader
        title={t('emailSubscriptions.title')}
        subtitle={t('emailSubscriptions.subtitle')}
        action={
          <Button
            variant="contained"
            sx={{
              bgcolor: '#B3EC49',
              color: 'black',
              '&:hover': { bgcolor: 'rgba(179, 236, 73, 0.8)' },
              borderRadius: '24px',
              px: 3,
            }}
            onClick={() => setSendModalOpen(true)}
          >
            {t('emailSubscriptions.sendEmail')}
          </Button>
        }
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          placeholder={t('emailSubscriptions.searchPlaceholder')}
          inputProps={{ 'aria-label': t('emailSubscriptions.searchPlaceholder') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label={t('emailSubscriptions.clearSearch')} onClick={() => setSearch('')}>
                  <X size={14} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ minWidth: 280, flexGrow: { xs: 1, sm: 0 }, ...filterFieldSx }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('emailSubscriptions.type')}</InputLabel>
          <Select
            value={filterType}
            label={t('emailSubscriptions.type')}
            onChange={(e) => setFilterType(e.target.value as EmailSubscriptionType | '')}
            sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
          >
            <MenuItem value="">{t('emailSubscriptions.all')}</MenuItem>
            <MenuItem value="NEWSLETTER">{t('emailSubscriptions.newsletter')}</MenuItem>
            <MenuItem value="PROJECT">{t('emailSubscriptions.project')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('emailSubscriptions.status')}</InputLabel>
          <Select
            value={filterStatus}
            label={t('emailSubscriptions.status')}
            onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}
            sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
          >
            <MenuItem value="">{t('emailSubscriptions.all')}</MenuItem>
            <MenuItem value="active">{t('emailSubscriptions.active')}</MenuItem>
            <MenuItem value="inactive">{t('emailSubscriptions.unsubscribed')}</MenuItem>
          </Select>
        </FormControl>
        <DateRangeFilter label={t('emailSubscriptions.subscribed')} value={subscribed} onChange={setSubscribed} />
        <Button
          variant="outlined"
          onClick={() => dispatch(loadEmailSubscriptions(query))}
          startIcon={<RefreshCw size={16} />}
          sx={{ borderRadius: '24px', px: 3 }}
        >
          {t('emailSubscriptions.refresh')}
        </Button>
        <Typography variant="body2" sx={{ ...mutedSx, ml: { sm: 'auto' } }}>
          {t('emailSubscriptions.total', { count: totalElements })}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid rgba(255, 255, 255, 0.08)', position: 'relative' }}>
        <Box sx={{ height: 2 }}>{isLoading && subscriptions && <LinearProgress sx={{ height: 2 }} />}</Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableHeaderCell column="EMAIL" {...sortProps}>
                  {t('emailSubscriptions.email')}
                </SortableHeaderCell>
                <SortableHeaderCell column="NAME" {...sortProps}>
                  {t('emailSubscriptions.account')}
                </SortableHeaderCell>
                <SortableHeaderCell column="TYPE" {...sortProps}>
                  {t('emailSubscriptions.type')}
                </SortableHeaderCell>
                <SortableHeaderCell column="ACTIVE" {...sortProps}>
                  {t('emailSubscriptions.status')}
                </SortableHeaderCell>
                <SortableHeaderCell column="CREATED_AT" {...sortProps}>
                  {t('emailSubscriptions.subscribed')}
                </SortableHeaderCell>
                <SortableHeaderCell column="UPDATED_AT" {...sortProps}>
                  {t('emailSubscriptions.lastChange')}
                </SortableHeaderCell>
                <TableCell align="right">{t('emailSubscriptions.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ opacity: isLoading && subscriptions ? 0.6 : 1, transition: 'opacity 150ms' }}>
              {!subscriptions && isLoading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {subscriptions && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasFilters ? t('emailSubscriptions.noMatches') : t('emailSubscriptions.noSubscriptionsFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((sub) => (
                <TableRow key={sub.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{sub.email ?? '—'}</TableCell>
                  <TableCell>{renderAccount(sub)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={sub.subscriptionType === 'NEWSLETTER' ? 'primary' : 'secondary'}
                      label={
                        sub.subscriptionType === 'NEWSLETTER'
                          ? t('emailSubscriptions.newsletter')
                          : sub.projectId !== null
                            ? t('emailSubscriptions.projectNumber', { id: sub.projectId })
                            : t('emailSubscriptions.project')
                      }
                    />
                  </TableCell>
                  <TableCell>{renderStatus(sub)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }} title={formatDateTime(sub.createdAt)}>
                    {formatDate(sub.createdAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }} title={formatDateTime(sub.updatedAt)}>
                    {formatDate(sub.updatedAt)}
                  </TableCell>
                  <TableCell align="right">
                    {!sub.active && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        startIcon={
                          isActivating && activatingId === sub.id ? <CircularProgress size={16} /> : <Check size={16} />
                        }
                        disabled={isActivating}
                        onClick={() => handleActivate(sub.id)}
                        sx={{ borderRadius: '16px', textTransform: 'none' }}
                      >
                        {t('emailSubscriptions.activate')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(_e: MouseEvent<HTMLButtonElement> | null, next: number) => setPage(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setPageSize(Number(e.target.value))
          }
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage={t('emailSubscriptions.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('emailSubscriptions.displayedRows', { from, to, count })}
        />
      </Paper>

      {/* Send Email Modal */}
      {sendModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !isSending) {
              e.stopPropagation();
              handleCloseSendModal();
            }
          }}
        >
          <div className="w-full h-full flex flex-col p-6" style={{ backgroundColor: greyP5 }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">{t('emailSubscriptions.sendEmail')}</h2>
              <button onClick={handleCloseSendModal} className="text-gray-400 hover:text-white" disabled={isSending}>
                <X size={20} />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-4">{t('emailSubscriptions.sendEmailDescription')}</p>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1, flexGrow: 1, minHeight: 0 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('emailSubscriptions.subscriptionType')}</InputLabel>
                <Select
                  value={sendType}
                  label={t('emailSubscriptions.subscriptionType')}
                  onChange={(e) => {
                    setSendType(e.target.value as EmailSubscriptionType | '');
                    if (e.target.value) {
                      setSendEmails([]);
                      setPasteEmails('');
                    }
                  }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
                >
                  <MenuItem value="">{t('emailSubscriptions.noneUseEmails')}</MenuItem>
                  <MenuItem value="NEWSLETTER">{t('emailSubscriptions.newsletter')}</MenuItem>
                  <MenuItem value="PROJECT">{t('emailSubscriptions.project')}</MenuItem>
                </Select>
              </FormControl>

              <Autocomplete
                multiple
                freeSolo
                options={allUsers.map((u: User) => u.email)}
                value={sendEmails}
                onChange={(_e, value) => {
                  setSendEmails(value as string[]);
                  if (value.length > 0) setSendType('');
                }}
                disabled={!!sendType}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t('emailSubscriptions.emailAddresses')}
                    placeholder={t('emailSubscriptions.typeOrSelectEmails')}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)' } }}
                  />
                )}
              />

              <TextField
                size="small"
                label={t('emailSubscriptions.pasteEmails')}
                value={pasteEmails}
                onChange={(e) => {
                  setPasteEmails(e.target.value);
                  if (e.target.value.trim().length > 0) setSendType('');
                }}
                disabled={!!sendType}
                multiline
                rows={3}
                placeholder="example1@email.com&#10;example2@email.com"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)' } }}
              />

              <TextField
                size="small"
                label={t('emailSubscriptions.subject')}
                value={sendSubject}
                onChange={(e) => setSendSubject(e.target.value)}
                fullWidth
                required
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)' } }}
              />

              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {t('emailSubscriptions.htmlContent')}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setShowPreview((prev) => !prev)}
                    startIcon={showPreview ? <Code size={16} /> : <Eye size={16} />}
                    sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}
                  >
                    {showPreview ? t('emailSubscriptions.editor') : t('emailSubscriptions.preview')}
                  </Button>
                </Box>
                {!showPreview ? (
                  <TextField
                    value={sendHtmlContent}
                    onChange={(e) => setSendHtmlContent(e.target.value)}
                    fullWidth
                    required
                    multiline
                    minRows={10}
                    placeholder="<html>...</html>"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.06)',
                        flexGrow: 1,
                        alignItems: 'flex-start',
                        '& textarea': { height: '100% !important' },
                      },
                      flexGrow: 1,
                      display: 'flex',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      border: '1px solid rgba(255,255,255,0.23)',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#fff',
                      flexGrow: 1,
                    }}
                  >
                    <iframe
                      srcDoc={sendHtmlContent || '<p style="color:#999;padding:16px;">No content</p>'}
                      title="Email Preview"
                      style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                      sandbox=""
                    />
                  </Box>
                )}
              </Box>

              {sendError && <Alert severity="error">{sendError}</Alert>}
              {sendSuccess && <Alert severity="success">{t('emailSubscriptions.emailsSentSuccessfully')}</Alert>}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button onClick={handleCloseSendModal} startIcon={<X size={20} />} disabled={isSending}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSendEmails}
                variant="contained"
                disabled={isSending}
                startIcon={isSending ? <CircularProgress size={20} /> : <Check size={20} />}
                sx={{
                  bgcolor: '#B3EC49',
                  color: 'black',
                  '&:hover': { bgcolor: 'rgba(179, 236, 73, 0.8)' },
                  borderRadius: '24px',
                  px: 4,
                }}
              >
                {t('emailSubscriptions.send')}
              </Button>
            </Box>
          </div>
        </div>
      )}
    </Box>
  );
};
